import { IS_CLIENT_SIDE, time } from '@dr.pogodin/react-utils';

import type {
  GetAggregatedUserScoresResT,
  GetHistoricalUserScoresResT,
} from 'server/controllers/user-scores';
import {
  getAggregatedUserScores,
  getHistoricalUserScores,
  getLatestUserScores,
} from 'services/user-scores';

import {
  type NumericUserScoreKindT,
  type UserScoreT,
  useAsyncData,
  useGlobalState,
} from 'utils';

type AggregatedScoresT = GetAggregatedUserScoresResT;
type HistoricalScoresT = GetHistoricalUserScoresResT;
type UserScoresT = Partial<Record<NumericUserScoreKindT, UserScoreT>>;

const HISTORICAL_SCORE_KINDS: NumericUserScoreKindT[] = [
  'TRANSFORMATION',
  'PRESENCE',
  'ENERGY',
  'INTENTION',
];

const LATEST_SCORE_KINDS: NumericUserScoreKindT[] = [
  ...HISTORICAL_SCORE_KINDS,
  'PRACTICE_STARTED_COUNT_LIFETIME',
  'PRACTICE_COMPLETION_COUNT_30D',
  'PRACTICE_COMPLETION_PERCENTAGE_30D',
];

export type UseUserScoresResT = {
  aggregated: GetAggregatedUserScoresResT | null;
  historical: GetHistoricalUserScoresResT | null;
  loading: boolean;
  scores: Partial<Record<NumericUserScoreKindT, UserScoreT>>;
};

export function useUserScores(
  options: { disabled?: boolean } = {},
): UseUserScoresResT {
  const [userId] = useGlobalState('user.id');

  if (!userId) throw Error('Unauthenticated');

  const disabled = options.disabled ?? !IS_CLIENT_SIDE;

  const e = useAsyncData(
    'userScores',
    async (): Promise<UserScoresT> => getLatestUserScores(
      userId,
      LATEST_SCORE_KINDS,
    ),
    {
      deps: [userId],
      disabled,
      maxage: time.MIN_MS,
    },
  );

  const a = useAsyncData(
    'userScoresAggregated',
    async (): Promise<AggregatedScoresT> => getAggregatedUserScores(
      userId,
    ),
    {
      deps: [userId],
      disabled,
      maxage: time.MIN_MS,
    },
  );

  const h = useAsyncData(
    'userScoresHistorical',
    async (): Promise<HistoricalScoresT> => getHistoricalUserScores(
      userId,
      HISTORICAL_SCORE_KINDS,
    ),
    {
      deps: [userId],
      disabled,
      maxage: time.MIN_MS,
    },
  );

  return {
    aggregated: a.data ?? null,
    historical: h.data ?? null,
    loading: e.loading || a.loading || h.loading,
    scores: e.data ?? {},
  };
}
